import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return '\u2014';
  return `$${amount.toFixed(2)}`;
}

export function formatDate(date: string | null | undefined): string {
  if (!date) return '\u2014';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateTime(date: string | null | undefined): string {
  if (!date) return '\u2014';
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Compact relative time for activity feeds: "just now", "5m ago", "3h ago",
 * "2d ago" \u2014 and an absolute date once it is more than a week old.
 */
export function formatRelativeTime(date: string | null | undefined): string {
  if (!date) return '\u2014';
  const diffMs = Date.now() - new Date(date).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 0) return 'just now';
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return formatDate(date);
}

/**
 * Tailwind text-color class for an inventory quantity: red at 0, amber when
 * low (under 10), normal otherwise. The ONE place the stock thresholds live \u2014
 * used by every quantity cell on the Products, Inventory and detail pages.
 */
export function inventoryColorClass(qty: number | null | undefined): string {
  if (qty === 0) return 'text-destructive';
  if (qty != null && qty < 10) return 'text-warning';
  return 'text-foreground';
}

// \u2500\u2500 Shopify / storefront URLs \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// Store: dancing-oaks-nursery.myshopify.com \u2014 public storefront at dancingoaks.com
/**
 * Human-readable option label for a variant — "#5 pot / Red" from its option
 * values, falling back to the variant title, then "Default" for the lone
 * variant of a single-variant product.
 */
export function variantOptionLabel(v: {
  option1: string | null;
  option2: string | null;
  option3: string | null;
  variant_title: string | null;
}): string {
  const opts = [v.option1, v.option2, v.option3].filter(Boolean);
  if (opts.length > 0) return opts.join(' / ');
  if (v.variant_title && v.variant_title !== 'Default Title') return v.variant_title;
  return 'Default';
}

const SHOPIFY_STORE_HANDLE = 'dancing-oaks-nursery';
const STOREFRONT_DOMAIN = 'dancingoaks.com';

/** Public storefront URL for a product (what customers see on the website). */
export function storefrontProductUrl(handle: string | null | undefined): string | null {
  if (!handle) return null;
  return `https://${STOREFRONT_DOMAIN}/products/${handle}`;
}

/** Shopify admin URL for editing a product. */
export function shopifyAdminProductUrl(shopifyProductId: number | string): string {
  return `https://admin.shopify.com/store/${SHOPIFY_STORE_HANDLE}/products/${shopifyProductId}`;
}

/** Shopify admin URL for viewing an order. */
export function shopifyAdminOrderUrl(shopifyOrderId: number | string): string {
  return `https://admin.shopify.com/store/${SHOPIFY_STORE_HANDLE}/orders/${shopifyOrderId}`;
}
